import { createContext, useContext, useState, useEffect, useRef } from 'react';

const PricePlanContext = createContext();

const PLAN_FALLBACKS = {
  'special': { name: 'Special Occasions', min: 1, max: 6, unit: 'days', price: 80 },
  'short-term': { name: 'Short Term', min: 1, max: 3, unit: 'weeks', price: 200 },
  'mid-term': { name: 'Mid Term', min: 1, max: 11, unit: 'months', price: 350 },
  'long-term': { name: 'Long Term', min: 1, max: 2, unit: 'years', price: 600 }
};

export const PricePlanProvider = ({ children }) => {
  const [selectedPlan, setSelectedPlan] = useState({
    name: '',
    duration: '',
    price: '',
    term: ''
  });

  const [pricingData, setPricingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const fetchInProgress = useRef(false);

  const updateSelectedPlan = (plan) => {
    setSelectedPlan(plan);
  };

  const selectPlanForTerm = (term) => {
    const fallback = PLAN_FALLBACKS[term];
    if (!fallback) return;
    const termData = pricingData?.[term];
    let info = fallback;
    if (termData && termData.length > 0) {
      const durations = termData.map(p => p.duration);
      info = {
        min: Math.min(...durations),
        max: term === 'mid-term' ? 11 : Math.max(...durations),
        unit: termData[0].unit + 's',
        price: Math.min(...termData.map(p => p.price))
      };
    }
    setSelectedPlan({
      name: fallback.name,
      duration: `${info.min}-${info.max} ${info.unit}`,
      price: `R ${Math.round(info.price)}`,
      term
    });
  };

  // Fetch pricing data once on mount
  useEffect(() => {
    if (fetchInProgress.current) return;
    
    fetchInProgress.current = true;
    setIsLoading(true);

    fetch(`${process.env.REACT_APP_API_URI}/v3/rates/calculate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ranges: [
          { term: 'special', min: 1, max: 6, unit: 'day' },
          { term: 'short-term', min: 1, max: 3, unit: 'week' },
          { term: 'mid-term', min: 1, max: 11, unit: 'month' },
          { term: 'long-term', min: 1, max: 2, unit: 'year' },
        ]
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.status === 'success' && data.data) {
          setPricingData(data.data);
        }
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error fetching pricing:', error);
        setIsLoading(false);
      });
  }, []);

  return (
    <PricePlanContext.Provider value={{ 
      selectedPlan, 
      updateSelectedPlan,
      selectPlanForTerm,
      pricingData,
      isLoading
    }}>
      {children}
    </PricePlanContext.Provider>
  );
};

export const usePricePlan = () => {
  const context = useContext(PricePlanContext);
  if (!context) {
    throw new Error('usePricePlan must be used within a PricePlanProvider');
  }
  return context;
}; 